import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-restaurant-line',
    title: 'Layouts que aumentam o faturamento',
    description: 'Mais mesas no mesmo espaço, circulação fluida para garçons e clientes, e cadeiras que convidam a ficar — mas não impedem o giro. Cada centímetro trabalhando pelo seu faturamento.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Materiais impermeáveis e fáceis de limpar',
    description: 'Tampos em granito, vidro ou laminado HPL que resistem a derramamentos. Cadeiras com courvin impermeável que seca com um pano. Higiene sem esforço no alto fluxo.',
  },
  {
    icon: 'ri-stack-line',
    title: 'Cadeiras empilháveis para eventos',
    description: 'Cadeiras que empilham até 8 unidades para liberar espaço em eventos, shows e formaturas. Leves, resistentes e com design que não envergonha o salão.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do salão',
    description: 'Veja o layout completo do seu salão em 3D antes de comprar qualquer móvel. Posicionamento de mesas, circulação de garçons e aproveitamento máximo do espaço.',
  },
];

const blocks = [
  {
    title: 'Layouts Inteligentes para Aumentar o Faturamento do Seu Salão',
    text: [
      'No negócio de gastronomia, faturamento é giro de mesa. Um salão mal planejado perde clientes na fila, tem garçons que se esbarram e mesas que ficam vazias enquanto outras têm fila. Nosso projeto 3D gratuito analisa o fluxo de clientes, a circulação da equipe e o posicionamento das mesas para maximizar a capacidade do salão sem comprometer o conforto.',
      'Para açaiterias e bares de alto fluxo, projetamos layouts com banquetas altas e mesas de bar que permitem rotatividade rápida, mesas de 2 e 4 lugares que se juntam para grupos maiores, e área de espera com bancos que não ocupam espaço de mesa. Para padarias e cafeterias, criamos balcões de atendimento que organizam o fluxo de pedidos e reduzem o tempo de espera.',
    ],
    img: '/media/ia/0341.webp',
    reverse: false,
  },
  {
    title: 'Cadeiras Empilháveis, Banquetas e Mesas que Resistem ao Batente',
    text: [
      'No alto fluxo da gastronomia, o mobiliário precisa resistir a centenas de usos por dia. Nossas cadeiras empilháveis têm estrutura em aço tubular de 25mm com solda contínua, assento e encosto em polipropileno injetado que não racha, e capacidade de 150kg. Empilham até 8 unidades para facilitar a limpeza do salão e liberar espaço para eventos.',
      'As banquetas para bar e açaiteria têm base em aço com protetor de piso, assento em courvin impermeável que resiste a álcool e sucos, e altura de 65cm ou 75cm conforme o balcão. Os tampos de mesa em granito ou vidro temperado resistem a derramamentos, manchas e o impacto de copos e pratos. Tudo projetado para durar anos no batente diário.',
    ],
    img: '/media/ia/0342.webp',
    reverse: true,
  },
  {
    title: 'Balcões de Atendimento e Áreas de Preparo que Organizam o Serviço',
    text: [
      'O balcão de atendimento é o coração da operação. Ele precisa de espaço para caixa, máquina de cartão e display de produtos, área de preparo com tampo resistente a calor e umidade, e organização interna que permite que a equipe trabalhe rápido sem se esbarrar. Nossos balcões são projetados para o fluxo específico do seu negócio — açaiteria, padaria, bar ou restaurante.',
      'Para padarias, fazemos vitrines refrigeradas embutidas no balcão, prateleiras para pães e bolos com iluminação LED que valoriza os produtos, e área de caixa separada do preparo para não misturar os fluxos. Para bares, projetamos balcões com cuba embutida, espaço para gelo e refrigerador sob o balcão. Tudo com projeto 3D gratuito antes de fabricar.',
    ],
    img: '/media/ia/0343.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0344.webp',
    alt: 'Cadeiras empilháveis para restaurante',
  },
  {
    src: '/media/ia/0345.webp',
    alt: 'Banquetas para bar e açaiteria',
  },
  {
    src: '/media/ia/0346.webp',
    alt: 'Mesas com tampo em granito',
  },
  {
    src: '/media/ia/0347.webp',
    alt: 'Balcão de atendimento para padaria',
  },
];

export default function GastronomiaPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Restaurantes, Açaiterias e Padarias | Caruaru PE',
    description: 'Cadeiras empilháveis, banquetas, mesas impermeáveis e layouts inteligentes para restaurantes, açaiterias, bares e padarias. Projeto 3D gratuito. Caruaru PE.',
    keywords: 'mobiliário restaurante Recife, cadeira empilhável restaurante Petrolina, banqueta bar Garanhuns, móveis açaiteria Arcoverde, móveis padaria Sertão PE, mesa restaurante Pernambuco, cadeira food service Agreste, mobiliário gastronomia Nordeste, mesa granito restaurante Recife, banqueta alta bar Petrolina, cadeira empilhável lanchonete Garanhuns, mobiliário restaurante Caruaru, mesa bar Bezerros',
    path: '/gastronomia',
  });

  useSchemaLD({
    id: 'schema-gastronomia',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Restaurantes, Açaiterias e Padarias | Caruaru PE',
      description: 'Cadeiras empilháveis, banquetas, mesas impermeáveis e layouts inteligentes para restaurantes, açaiterias, bares e padarias. Projeto 3D gratuito.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/gastronomia`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Restaurantes e Gastronomia',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0348.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Gastronomia · Açaiterias · Bares · Padarias
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Layouts inteligentes para aumentar o faturamento do seu salão.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Cadeiras empilháveis, banquetas modernas e mesas com tampo impermeável. Mais giro, menos esforço na limpeza e um salão que seus clientes querem voltar.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Gastronomia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que restaurantes de Caruaru escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Mobiliário de Alto Fluxo</span> para Gastronomia
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Gastronomia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">Veja o layout do seu salão em 3D antes de comprar qualquer móvel.</h2>
          <p className="text-gray-400 text-base mb-10">Nossa equipe visita seu estabelecimento, mede o espaço e entrega o projeto 3D em até 48h. Você aprova, a gente fabrica e instala.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Gastronomia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Mobiliário para <span className="text-[#E8610A]">Gastronomia</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Gastronomia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Gastronomia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
